import { useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ChartBar, PieChart as PieIcon } from 'lucide-react'

import { formatMoney } from '@/lib/format'
import type { MonthlyTotal, PnL, TransactionCategory } from '@/types/finance'
import { CHART_AXIS_TICK, CHART_GRID_PROPS, CHART_TOOLTIP_PROPS } from '@/features/dashboard/components/chart-theme'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-')
  return `${MONTHS[Number(m) - 1]} ${y.slice(2)}`
}

interface TrendChartProps {
  trend?: MonthlyTotal[]
  isLoading: boolean
}

export function TrendChart({ trend, isLoading }: TrendChartProps) {
  const data = useMemo(
    () => (trend ?? []).map((t) => ({ month: monthLabel(t.month), income: t.income, expense: t.expense })),
    [trend],
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ChartBar className="h-4 w-4" /> Monthly trend
        </CardTitle>
        <CardDescription>Income vs expenses per month</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[240px] w-full" />
        ) : data.length === 0 ? (
          <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
            No ledger data
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data}>
              <CartesianGrid {...CHART_GRID_PROPS} />
              <XAxis dataKey="month" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                {...CHART_TOOLTIP_PROPS}
                formatter={(value, name) => [formatMoney(Number(value)), String(name)]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="income" name="Income" fill="hsl(var(--success))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" name="Expenses" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}

const CATEGORY_FILLS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
]

interface CategoryPieProps {
  pnl?: PnL
  isLoading: boolean
  kind: 'revenue' | 'expenses'
}

export function CategoryPie({ pnl, isLoading, kind }: CategoryPieProps) {
  const lines = kind === 'revenue' ? (pnl?.revenue ?? []) : (pnl?.expenses ?? [])
  const data = lines.map((l) => ({ name: l.label, value: l.amount }))

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <PieIcon className="h-4 w-4" /> {kind === 'revenue' ? 'Revenue by source' : 'Expenses by category'}
        </CardTitle>
        <CardDescription>
          {kind === 'revenue' ? 'Where income came from' : 'Where the money went'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[240px] w-full" />
        ) : data.length === 0 ? (
          <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
            Nothing to show
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Tooltip
                {...CHART_TOOLTIP_PROPS}
                formatter={(value, name) => [formatMoney(Number(value)), String(name)]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                {data.map((entry) => (
                  <Cell key={entry.name as TransactionCategory} fill={CATEGORY_FILLS[data.indexOf(entry) % CATEGORY_FILLS.length]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
